import React, {FC, useEffect, useState, useCallback} from 'react'
import {Formik, Form} from 'formik'
import * as Yup from 'yup'
import {ToastContainer} from 'react-toastify' 
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import FormTabHeader, {TabOption} from '../../../../shared/components/FormTabHeader'
import TextInput from '../../../../shared/components/TextInput'
import SelectInput from '../../../../shared/components/SelectInput'
import MultiSelectCombobox from '../../../../shared/components/MultiSelectCombobox'
import {IXmlTariffModel, APPROVAL_STATUS_OPTIONS} from '../xml-tariff/XmlTariffModel'
import {loadPendingApprovals, approveXmlTariff, rejectXmlTariff} from './Api'
import {XmlTariffApprovalTable} from './XmlTariffApprovalTable'
import {
  reservationSources,
  branches,
} from '../../../../shared/models/SharedApiLookups'
import {useHistory} from 'react-router-dom'

const tabOptions: TabOption[] = [
  {key: 'list', title: 'Onay Bekleyen Tarifeler', icon: 'bi bi-check2-square'},
]

const emptyValidation = Yup.object().shape({})
const emptyInitial = {}

export const XmlTariffApprovalPage: FC = () => {
  const [loading, setLoading] = useState(false)
  const [tariffs, setTariffs] = useState<IXmlTariffModel[]>([])
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [sortBy, setSortBy] = useState<string>('createdDate')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [tab, setTab] = useState('list')
  const [filterOpen, setFilterOpen] = useState(true)
  const history = useHistory()

  const [confirmModal, setConfirmModal] = useState<{
    show: boolean
    type: 'approve' | 'reject'
    tariff: IXmlTariffModel | null
  }>({show: false, type: 'approve', tariff: null})

  const [reservationSourceOptions, setReservationSourceOptions] = useState<
    {value: string; label: string}[]
  >([])
  const [branchOptions, setBranchOptions] = useState<{value: string; label: string}[]>([])

  useEffect(() => {
    reservationSources().then(setReservationSourceOptions)
    branches().then((brn) => setBranchOptions([{value: '0', label: 'Hepsi'}, ...brn]))
  }, [])

  const fetchPending = useCallback(
    async (page: number = 0, sort?: string, order?: 'asc' | 'desc') => {
      setLoading(true)
      try {
        const res = await loadPendingApprovals(page, 10, sort || sortBy, order || sortOrder)
        if (res.data.hasError) {
          throw new Error(res.data.message || 'Tarifeler yüklenemedi')
        }
        setTariffs(res.data.payload.content)
        setTotalPages(res.data.payload.totalPages)
      } catch (err) {
        showErrorToast(err instanceof Error ? err.message : 'Tarifeler yüklenemedi')
      } finally {
        setLoading(false)
      }
    },
    [sortBy, sortOrder]
  )

  useEffect(() => {
    fetchPending(0)
  }, [fetchPending])

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        setCurrentPage(newPage)
        fetchPending(newPage, sortBy, sortOrder)
      }
    },
    [totalPages, sortBy, sortOrder, fetchPending]
  )

  const handleSort = useCallback(
    (column: string, order: 'asc' | 'desc') => {
      setSortBy(column)
      setSortOrder(order)
      fetchPending(currentPage, column, order)
    },
    [currentPage, fetchPending]
  )

  const handleApproveClick = (tariff: IXmlTariffModel) => {
    setConfirmModal({show: true, type: 'approve', tariff})
  }

  const handleRejectClick = (tariff: IXmlTariffModel) => {
    setConfirmModal({show: true, type: 'reject', tariff})
  }

  const handleDetailClick = (tariff: IXmlTariffModel) => {
    history.push(`/definition/xml-tariff-detail?tariffId=${encodeURIComponent(tariff.id)}`)
  }

  const handleConfirm = async () => {
    const {type, tariff} = confirmModal
    if (!tariff) return

    setLoading(true)
    try {
      setConfirmModal({show: false, type: 'approve', tariff: null})
      const res =
        type === 'approve'
          ? await approveXmlTariff(tariff.id)
          : await rejectXmlTariff(tariff.id)

      if (res.data.hasError) {
        showErrorToast(res.data.message || 'İşlem sırasında hata oluştu')
        return
      }

      showSuccessToast(
        type === 'approve' ? 'Tarife başarıyla onaylandı' : 'Tarife reddedildi'
      )
      await fetchPending(currentPage, sortBy, sortOrder)
    } catch {
      showErrorToast('İşlem sırasında hata oluştu')
    } finally {
      setLoading(false) 
    }
  }

  return (
    <>
      <Formik
        initialValues={emptyInitial}
        validationSchema={emptyValidation}
        onSubmit={() => {}}
      >
        {() => (
          <>
            <FormTabHeader
              formId='xml-tariff-approval-form'
              isVisibleResetButton={false}
              isVisibleSubmitButton={false}
              isVisibleExportButton={false}
              formikSubmit={() => {}}
              formikReset={() => {}}
              loading={loading}
              tab={tab}
              setTab={setTab}
              setSelectedId={() => {}}
              tabs={tabOptions}
              onExportExcel={() => console.log('Excel export')}
            />

            <div className='card'>
              <div className='card-header border-0 pt-6'>
                <div className='card-title'>
                  <div className='d-flex align-items-center gap-2 my-1'>
                    <i className='bi bi-funnel fs-1 text-primary'></i>
                    <h3 className='fw-bold m-0'>Onay Filtreleri</h3>
                  </div>
                </div>
                <div className='card-toolbar'>
                  <button
                    type='button'
                    className='btn btn-sm btn-light-primary'
                    onClick={() => setFilterOpen((prev) => !prev)}
                  >
                    <i className={`ki-duotone ki-${filterOpen ? 'up' : 'down'} fs-2`}>
                      <span className='path1'></span>
                      <span className='path2'></span>
                    </i>
                    {filterOpen ? 'Gizle' : 'Göster'}
                  </button>
                </div>
              </div>

              <div className={`collapse ${filterOpen ? 'show' : ''}`}>
                <div className='card-body border-top pt-6'>
                  <Form>
                    <div className='row g-6 mb-6 align-items-end'>
                      <div className='col-md-3'>
                        <MultiSelectCombobox
                          name='filterBranch'
                          label='Şube'
                          placeholder='Şube Seçiniz'
                          options={branchOptions}
                        />
                      </div>
                      <div className='col-md-3'>
                        <MultiSelectCombobox
                          name='filterReservationSource'
                          label='Rezervasyon Kaynağı'
                          placeholder='Rez. Kaynağı Seçiniz'
                          options={reservationSourceOptions}
                        />
                      </div>
                      <div className='col-md-3'>
                        <SelectInput
                          name='filterApprovalStatus'
                          label='Onay Durumu'
                          options={APPROVAL_STATUS_OPTIONS}
                        />
                      </div>
                      <div className='col-md-3'>
                        <TextInput name='filterRateName' label='Tarife Adı' />
                      </div>
                    </div>
                    <div className='row g-6 mb-0 align-items-end'>
                      <div className='col-md-3'>
                        <TextInput name='filterStartDate' type='date' label='Başlangıç Tarihi' />
                      </div>
                      <div className='col-md-3'>
                        <TextInput name='filterEndDate' type='date' label='Bitiş Tarihi' />
                      </div>
                      <div className='col-md-3'>
                        <button
                          type='button'
                          className='btn btn-info d-flex align-items-center justify-content-center gap-2 w-100'
                          onClick={() => fetchPending(0)}
                        >
                          <i className='bi bi-funnel'></i> Filtrele
                        </button>
                      </div>
                    </div>
                  </Form>
                </div>
              </div>

              <div className='card-body pt-0'>
                <XmlTariffApprovalTable
                  data={tariffs}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  onApprove={handleApproveClick}
                  onReject={handleRejectClick}
                  onDetail={handleDetailClick}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                  loading={loading}
                />
              </div>
            </div>
          </>
        )}
      </Formik>

      <ConfirmModal
        show={confirmModal.show}
        header={confirmModal.type === 'approve' ? 'Onay' : 'Red Onayı'}
        text={
          confirmModal.type === 'approve'
            ? `"${confirmModal.tariff?.rateName}" tarifesini onaylamak istediğinize emin misiniz?`
            : `"${confirmModal.tariff?.rateName}" tarifesini reddetmek istediğinize emin misiniz?`
        }
        handleClose={() => setConfirmModal({show: false, type: 'approve', tariff: null})}
        applyOnClick={handleConfirm}
      />

      <ToastContainer
        position='top-right'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  )
}
